// Admin: Benutzer ↔ Abteilung + Rol + Ticket-Bereiche atama
import { useState } from 'react';
import { useApi } from '../hooks/useApi';
import { api, TICKET_BEREICH_GRUPPEN, type User } from '../lib/api';

// Mehrfach-Auswahl der Bereiche, deren Tickets ein Benutzer sehen darf.
function TicketBereichPicker({ value, onSave, disabled }: { value: string; onSave: (v: string) => void; disabled?: boolean }) {
  const [open, setOpen] = useState(false);
  const selected = (value || '').split(',').map(s => s.trim()).filter(Boolean);
  const toggle = (b: string) => {
    const next = selected.includes(b) ? selected.filter(x => x !== b) : [...selected, b];
    onSave(next.join(','));
  };
  return (
    <div className="relative">
      <button disabled={disabled} onClick={() => setOpen(o => !o)}
              className="text-xs border border-slate-200 rounded px-2 py-1 bg-white hover:bg-slate-50 max-w-[240px] truncate text-left">
        {selected.length ? selected.join(', ') : <span className="text-slate-400">nur eigene</span>} ▾
      </button>
      {open && (
        <>
          <div className="fixed inset-0 z-40" onClick={() => setOpen(false)} />
          <div className="absolute z-50 mt-1 w-64 bg-white border border-slate-200 rounded-lg shadow-xl p-2 max-h-72 overflow-y-auto">
            {TICKET_BEREICH_GRUPPEN.map(g => (
              <div key={g.gruppe} className="mb-1.5">
                <div className="text-[10px] uppercase tracking-wide text-slate-400 px-1 py-0.5">{g.gruppe}</div>
                {g.bereiche.map(b => (
                  <label key={b} className="flex items-center gap-2 px-1 py-1 rounded hover:bg-slate-50 cursor-pointer text-sm">
                    <input type="checkbox" checked={selected.includes(b)} onChange={() => toggle(b)} />
                    <span>{b}</span>
                  </label>
                ))}
              </div>
            ))}
          </div>
        </>
      )}
    </div>
  );
}

export function Einstellungen() {
  const users = useApi(() => api.lookup.users(), []);
  const abt   = useApi(() => api.lookup.abteilungen(), []);
  const [savingId, setSavingId] = useState<number | null>(null);
  const [toast, setToast] = useState<string | null>(null);

  const flash = (s: string) => { setToast(s); setTimeout(() => setToast(null), 2000); };

  const update = async (id: number, patch: { abteilung_id?: number | null; role?: string; ticket_bereiche?: string }) => {
    setSavingId(id);
    try {
      await api.lookup.updateUser(id, patch);
      flash('Gespeichert');
      users.reload();
    } catch (e) {
      flash(e instanceof Error ? e.message : 'Fehler');
    } finally {
      setSavingId(null);
    }
  };

  return (
    <div className="p-8 max-w-5xl">
      {toast && <div className="fixed top-4 right-4 bg-emerald-600 text-white rounded-lg px-4 py-2 shadow-lg text-sm z-50">{toast}</div>}

      <header className="mb-6">
        <div className="text-xs uppercase tracking-wider text-slate-500">Einstellungen</div>
        <h1 className="text-2xl font-bold text-slate-900">Benutzer &amp; Abteilungen</h1>
        <p className="text-sm text-slate-500 mt-1">
          Jedem Mitarbeiter eine Abteilung zuweisen — damit er in der Werkstatt-Auswahl erscheint
          und nur die Aufträge seiner Abteilung sieht. <strong>Sachbearbeiter</strong> / Admin: Abteilung leer lassen.
        </p>
      </header>

      <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-xs text-slate-600 uppercase">
            <tr>
              <th className="text-left px-5 py-2 font-medium">Name</th>
              <th className="text-left px-5 py-2 font-medium">Kürzel</th>
              <th className="text-left px-5 py-2 font-medium">Rolle</th>
              <th className="text-left px-5 py-2 font-medium">Abteilung</th>
              <th className="text-left px-5 py-2 font-medium">Ticket-Bereiche</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {users.data?.data.filter(u => u.kurzel !== 'SYS' && !u.name.startsWith('[WS]')).map((u: User) => (
              <tr key={u.id} className={`hover:bg-slate-50 ${savingId === u.id ? 'opacity-60' : ''}`}>
                <td className="px-5 py-2 font-medium">{u.name}</td>
                <td className="px-5 py-2 font-mono text-xs">{u.kurzel ?? '—'}</td>
                <td className="px-5 py-2">
                  <select
                    value={u.role}
                    onChange={e => update(u.id, { role: e.target.value })}
                    className="text-sm border border-slate-200 rounded px-2 py-1 bg-white">
                    <option value="mitarbeiter">Mitarbeiter</option>
                    <option value="meister">Meister</option>
                    <option value="admin">Admin</option>
                  </select>
                </td>
                <td className="px-5 py-2">
                  <select
                    value={u.abteilung_id ?? ''}
                    onChange={e => update(u.id, { abteilung_id: e.target.value ? Number(e.target.value) : null })}
                    className="text-sm border border-slate-200 rounded px-2 py-1 bg-white"
                    style={{
                      background: u.abteilung_farbe ? `${u.abteilung_farbe}22` : undefined,
                      borderColor: u.abteilung_farbe ? `${u.abteilung_farbe}88` : undefined,
                    }}>
                    <option value="">— keine (= Sachbearbeiter / Büro) —</option>
                    {abt.data?.data.map(a => (
                      <option key={a.id} value={a.id}>{a.name}</option>
                    ))}
                  </select>
                </td>
                <td className="px-5 py-2">
                  {u.role === 'admin'
                    ? <span className="text-xs text-slate-400 italic">alles (Admin)</span>
                    : <TicketBereichPicker value={u.ticket_bereiche ?? ''} disabled={savingId === u.id}
                        onSave={(v) => update(u.id, { ticket_bereiche: v })} />}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="mt-6 bg-amber-50 border border-amber-200 rounded-xl p-4 text-sm text-amber-900">
        <strong>Hinweis:</strong> Nach Abteilungs-Zuweisung erscheinen diese Mitarbeiter in der Werkstatt-Auswahl
        („Wer bist du?"). Benutzer ohne Abteilung (= Büro / SB) sind dort ausgeblendet, sehen aber im Dashboard alle Abteilungen.
      </div>

      <div className="mt-3 bg-sky-50 border border-sky-200 rounded-xl p-4 text-sm text-sky-900">
        <strong>Ticket-Bereiche:</strong> Bestimmt, welche Tickets ein Benutzer in der Ticket-Liste sieht — nur die
        Bereiche, für die er zuständig ist, plus seine eigenen und ihm zugewiesenen Tickets. <strong>Ohne Zuweisung</strong>
        („nur eigene") sieht er ausschließlich selbst gemeldete/übernommene Tickets. <strong>Admins sehen alles.</strong>
      </div>
    </div>
  );
}
